import axios from 'axios';
import { axiosConfig } from './config';

// Create axios instance with default config
const api = axios.create(axiosConfig);

// Add request interceptor to add auth token
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Add response interceptor to handle 401s
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            const url = error.config?.url || '';
            // Don't touch auth state for login attempts — let the form handle it
            if (!url.endsWith('/token')) {
                // Only clear token and redirect if the token is actually expired.
                // A 401 with a still-valid token could be a transient server issue;
                // clearing the token in that case would log the user out unnecessarily.
                if (!isAuthenticated()) {
                    localStorage.removeItem('token');
                    sessionStorage.clear();
                    window.location.href = import.meta.env.BASE_URL + 'login';
                }
            }
        }
        return Promise.reject(error);
    }
);

/**
 * Check if the user is authenticated by decoding the JWT and checking expiry.
 */
export function isAuthenticated(): boolean {
    const token = localStorage.getItem('token');
    if (!token) return false;
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const exp = payload.exp;
        if (!exp) return false;
        return Date.now() < exp * 1000;
    } catch {
        return false;
    }
}

/**
 * Get the current user's email from the JWT token.
 */
export function getUserEmail(): string | null {
    const token = localStorage.getItem('token');
    if (!token) return null;
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.sub || null;
    } catch {
        return null;
    }
}

/**
 * Check if the current user has admin privileges from the JWT payload.
 */
export function isAdmin(): boolean {
    const token = localStorage.getItem('token');
    if (!token) return false;
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.is_admin === true;
    } catch {
        return false;
    }
}

export default api;
